<template>
  <div v-if="topic">
    <div v-if="canCommentOnThoughts(topic)">
      <div class="alert alert-info" v-if="infotext !== ''">{{ infotext }}</div>
      <div class="alert alert-danger" v-if="serverErr !== ''">{{ serverErr }}</div>

      <InfoRequiredFields />

      <div id="wdw-forum-thought-commentform">
        <Form @submit="onSubmitComment">
          <div id="wdw-comment-form" class="wdw-form">
            <form-control-textarea
              label="Ihr Beitrag"
              name="content"
              code="content"
              parentId="wdw-forum-thought-commentform"
              error="Beitrag kann nicht leer sein"
              :defaultvalue="defaultContent"
              :required="true"
              :rows="4"
              :textLimitConfig="{
                maxCount: project.comment_maxlength,
                cLimitText: 'Maximale Zeichenanzahl erreicht',
              }"
            />

            <form-control-checkbox
              v-if="showHighlightedCheckbox && isAdmin"
              label="Beitrag hervorherben"
              name="highlight"
              code="highlight"
              parentId="wdw-forum-thought-commentform"
              :defaultvalue="defaultHighlighted"
            />
            <form-control-file
              v-if="toBool(topic.allow_comment_attachments)"
              ref="attachmentRef"
              name="attachment-file"
              code="attachment-file"
              :help="maxAttachmentSizeHint"
              label="Hier können Sie ein Bild hochladen"
              parentId="wdw-forum-thought-commentform"
              accepts="image/*"
              v-on:validate="onValidatedFormField"
              :fileSizeLimitByte="project ? project.discuss.attachment_max_size : 0"
            />

            <div v-if="comment && comment.files && comment.files.length > 0">
              <p>Bereits angehängte Bilder:</p>
              <p>
                <span v-for="imgfile in comment.files" v-bind:key="imgfile.id" style="margin: 5px">
                  <img :src="imgfile.url" width="200" class="image-preview" :title="imgFileTitle(imgfile)" />
                </span>
              </p>
            </div>

            <button type="submit" name="submit" class="btn btn-primary btn-form-submit" :disabled="loaderSubmitComment">
              Speichern
            </button>
            &nbsp;
            <button type="button" class="ml-2 btn btn-default btn-form-cancel" @click="onCancelPressed()">
              Abbrechen
            </button>
            <loading v-if="loaderSubmitComment" />
          </div>
        </Form>
      </div>
    </div>
    <div v-else-if="canCommentAfterLogin(topic)" class="alert alert-info">
      Sie müssen sich erst einloggen um einen Kommentar abzuschicken
    </div>
  </div>
</template>
<script>
import { ref, toRefs } from '@vue/reactivity';
import { computed } from '@vue/runtime-core';
import { Form } from 'vee-validate';

// stores
import { userStore } from '@/store/user-store';
import { projectStore } from '@/store/project-store';

// components
import Loading from '@/components/general/Loading';
import FormControlFile from '@/components/general/FormControl/File.vue';
import FormControlTextarea from '@/components/general/FormControl/TextArea.vue';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox.vue';
import InfoRequiredFields from '@/components/general/InfoRequiredFields.vue';

// composables
import useToggler from '@/composables/general/useToggler';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useCommentEvents from '@/composables/comment/useCommentEvents';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useConverter from '@/composables/general/useConverter';

// respositories
import { RepositoryFactory } from '@/api/repositoryFactory';
const CommentRepository = RepositoryFactory.get('comment');

export default {
  name: 'forum-comment-form',

  props: {
    thoughtid: {
      type: Number,
      required: true,
    },
    topicid: {
      required: true,
      type: Number,
    },
    commentid: {
      type: Number,
      required: false,
      default: null,
    },
    comment: {
      type: Object,
      required: false,
      default: null,
    },
    infotext: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['comment-updated', 'comment-form-close'],

  setup(props, context) {
    const { thoughtid, comment, commentid } = toRefs(props);
    const { emitCommentUpdated, emitCommentFormClose } = useCommentEvents(context);
    const { canCommentOnThoughts, canCommentAfterLogin } = useTopicConfig();
    const { toggleValue: loaderSubmitComment, toggle: toggleLoaderSubmitComment } = useToggler(false);
    const { l } = useLocalizeMessages();
    const { toBool, bytesToHumanReadable } = useConverter();

    // PM wants highlighted to be hidden completely for now
    const showHighlightedCheckbox = false;
    const serverErr = ref('');
    // Contains detailed values/files from form
    const uploadedFiles = {};
    const attachmentRef = ref(null);

    // Computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isAdmin = computed(() => {
      return userStore.isAdmin();
    });
    const topic = computed(() => {
      return projectStore.getFetchedTopic(props.topicid);
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const defaultContent = computed(() => {
      if (comment && comment.value) {
        return comment.value.content;
      }
      return '';
    });
    const defaultHighlighted = computed(() => {
      if (comment && comment.value && comment.value.highlight) {
        return true;
      }
      return false;
    });
    const isInEditMode = computed(() => {
      return comment && comment.value && comment.value.commentid;
    });
    const maxAttachmentSizeHint = computed(() => {
      if (project.value && project.value.discuss && project.value.discuss.attachment_max_size) {
        return (
          'Die maximal zulässige Dateigröße beträgt ' + bytesToHumanReadable(project.value.discuss.attachment_max_size)
        );
      }
      return '';
    });
    // Methods
    const showError = error => {
      const errMsg = error.response.data.message;
      serverErr.value = l(errMsg);
      if (errMsg.indexOf('attachment') >= 0) {
        attachmentRef.value?.deleteFile();
      }
    };
    const onSubmitComment = (param, actions) => {
      serverErr.value = '';
      toggleLoaderSubmitComment();

      const files = new Map();
      for (const [key, value] of Object.entries(uploadedFiles)) {
        if (value.value) {
          files.set(key, value.value);
        }
      }

      if (commentid.value) {
        // update comment
        CommentRepository.update(thoughtid.value, commentid.value, param, files)
          .then(resp => {
            toggleLoaderSubmitComment();
            emitCommentUpdated(resp);
          })
          .catch(err => {
            toggleLoaderSubmitComment();
            showError(err);
            console.log(err);
          });
      } else {
        // create new comment
        CommentRepository.create(thoughtid.value, param, files)
          .then(resp => {
            toggleLoaderSubmitComment();
            emitCommentUpdated(resp);
          })
          .catch(err => {
            toggleLoaderSubmitComment();
            showError(err);
            console.log(err);
          });
      }
      actions.resetForm();
    };
    const imgFileTitle = file => {
      return file.title + ' (' + file.url + ')';
    };
    const onValidatedFormField = fieldValue => {
      if (fieldValue.type === 'file') {
        uploadedFiles[fieldValue.name] = fieldValue;
      }
    };

    const onCancelPressed = () => {
      emitCommentFormClose();
    };

    return {
      // VARS
      loaderSubmitComment,
      serverErr,
      showHighlightedCheckbox,
      // Computed
      topic,
      project,
      user,
      isAdmin,
      defaultContent,
      defaultHighlighted,
      isInEditMode,
      maxAttachmentSizeHint,
      // Methods
      canCommentOnThoughts,
      canCommentAfterLogin,
      toggleLoaderSubmitComment,
      onSubmitComment,
      onCancelPressed,
      imgFileTitle,
      toBool,
      onValidatedFormField,
    };
  },

  components: {
    Form,
    Loading,
    FormControlTextarea,
    FormControlCheckbox,
    FormControlFile,
    InfoRequiredFields,
  },
};
</script>
